<template>
  <login-layout :loading="!tokenChecked">
    <template #body>
      <atom-info-box v-if="tokenMissing" priority="danger">
        This token link is invalid. Make sure it is the same as the one provided in the email you received.
      </atom-info-box>

      <atom-info-box v-else-if="!tokenValid" priority="danger">
        The reset token used was invalid. This means that the token may have expired or was already used.
      </atom-info-box>

      <template v-else>
        <p>To reset your password, please enter a new one below. If you no longer want to reset your password you can cancel.</p>

        <password-strength-checker/>
        <atom-info-box v-if="validationError.message" priority="danger" role="alert">
          {{ validationError.message }}
        </atom-info-box>

        <atom-info-box v-else-if="resetPasswordResult?.message" priority="danger" role="alert">
          {{ resetPasswordResult?.message }}
        </atom-info-box>

        <atom-info-box v-else-if="resetPasswordFailure" priority="danger" role="alert">
          Unfortunately, there was an internal error processing this request. Please speak to your local support desk.
        </atom-info-box>

        <atom-text-input id="password" v-model="password" type="password" label="New password"
                         :error-message="validationError.password"/>

        <atom-text-input id="password-confirm" v-model="passwordConfirmation" type="password" label="Confirm new password"
                         :error-message="validationError.passwordConfirmation"/>

        <div class="ecase-sign-in__form-action-group">
          <atom-button label="Set password" type="primary" :disabled="loading || undefined" @click="submitResetPassword"/>
          <atom-button label="Cancel" type="link-button" :disabled="loading || undefined" @click="navigateToLogin"/>
        </div>
      </template>
    </template>
  </login-layout>
</template>

<script>
import { useVuelidate } from "@vuelidate/core";
import { required, sameAs } from "@vuelidate/validators";
import PasswordStrengthChecker from "authentication/components/login/components/PasswordStrengthChecker.vue";
import LoginLayout from "authentication/components/login/layout/LoginLayout.vue";
import { canResetPassword, resetPassword } from "authentication/js/api/password.api";
import { useChangePasswordStore } from "authentication/js/composable/change-password.store.ts";
import AtomButton from "core/components/atom/button/AtomButton.vue";
import AtomInfoBox from "core/components/atom/infoBox/AtomInfoBox.vue";
import AtomTextInput from "core/components/atom/textInput/AtomTextInput.vue";
import announce from "core/js/announce";
import { getFirstValidationError } from "core/js/validation/validation-messages.js";
import { template } from "lodash";
import { storeToRefs } from "pinia";

const emptyValidationError = {
  currentPassword: "",
  password: "",
  passwordConfirmation: "",
  message: "",
};

export default {
  components: {
    AtomTextInput,
    PasswordStrengthChecker,
    AtomButton,
    LoginLayout,
    AtomInfoBox,
  },
  setup() {
    const {
      password,
      passwordConfirmation,
      passwordMeetsSecurityCriteria,
    } = storeToRefs(useChangePasswordStore());

    return {
      password,
      passwordConfirmation,
      passwordMeetsSecurityCriteria,
      v$: useVuelidate(),
    };
  },
  data() {
    return {
      loading: false,
      tokenChecked: false,
      tokenValid: false,
      tokenMissing: false,
      resetPasswordResult: undefined,
      validationError: emptyValidationError,
    };
  },
  validations() {
    return {
      password: { required },
      passwordConfirmation: {
        required,
        sameAsPassword: sameAs(this.password),
      },
    };
  },
  computed: {
    resetPasswordSuccess() {
      return this.resetPasswordResult?.resetPasswordStatus === "SUCCESS";
    },
    resetPasswordFailure() {
      return this.resetPasswordResult?.resetPasswordStatus === "FAILURE";
    },
  },
  async created() {
    if (!this.$route.query.token) {
      this.tokenMissing = true;
    } else {
      this.tokenValid = await canResetPassword(this.$route.query.token);
    }

    this.tokenChecked = true;
  },
  methods: {
    navigateToLogin() {
      this.$router.push({ name: "login-page" });
    },
    async submitResetPassword() {
      this.loading = true;
      this.resetPasswordResult = {};

      try {
        if (await this.v$.$validate() && this.passwordMeetsSecurityCriteria) {
          this.resetPasswordResult = await resetPassword(this.password, this.$route.query.token);

          if (this.resetPasswordSuccess) {
            await this.$router.push({
              name: "login-page",
              query: {
                passwordChanged: "true",
              },
            });
          }
        } else {
          this.displayValidationErrors();
          announce(`There were some errors with your submission. ${this.validationError.password || ""}, `
            + `${this.validationError.passwordConfirmation || ""}`);
        }
      } finally {
        this.loading = false;
      }
    },
    displayValidationErrors() {
      this.validationError = {
        password: getFirstValidationError(
          this.v$.password,
          "New password",
          {
            required: template("Enter a new password"),
          },
        ),
        passwordConfirmation: getFirstValidationError(
          this.v$.passwordConfirmation,
          "Confirm new password",
          {
            required: template("Enter your new password again"),
            sameAsPassword: template("The password confirmation does not match"),
          },
        ),
        message: "",
      };

      if (!this.passwordMeetsSecurityCriteria && !this.validationError.password) {
        this.validationError = {
          ...this.validationError,
          message: "The password entered does not conform to the password policy",
        };
      }
    },
  },
};
</script>
